import { useMicRecorder } from "../../../hooks/use-mic-recorder";
import { useEffect, useMemo, useRef } from "react";

interface Point {
  x: number;
  y: number;
  radian: number;
  range: number;
  phase: number;
}

interface OrbProps {
  thinking: boolean;
  size?: number; // Size in pixels
}

// Add type declaration for webkit prefix
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

export const Orb = ({ thinking = false, size = 30 }: OrbProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isInitializing } = useMicRecorder();
  const tickRef = useRef(0);
  const animationFrameRef = useRef<number>(0);

  const intensity = useMemo(() => {
    return thinking || isInitializing ? 250 : 1;
  }, [isInitializing, thinking]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const pi = Math.PI;
    const points = 5;
    const scale = size / 30; // Base scale factor from default size
    const radius = 12 * scale;

    const w = size;
    const h = size;
    canvas.width = w * dpr;
    canvas.height = h * dpr;

    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);

    // Center coordinates (in CSS pixels, not canvas pixels)
    const center = {
      x: w / 2,
      y: h / 2
    };

    const circles: Array<Array<Point>> = [];
    const rangeMin = 0.5 * scale;
    const rangeMax = 1.5 * scale;

    // Create gradients
    const gradient1 = ctx.createLinearGradient(0, 0, w, 0);
    gradient1.addColorStop(0, "#9A26D1"); // bright purple
    gradient1.addColorStop(1, "#F37037"); // orange

    const gradient2 = ctx.createLinearGradient(0, 0, w, 0);
    gradient2.addColorStop(0, "#EF4269"); // red
    gradient2.addColorStop(1, "#EEDDF7"); // white

    const gradient3 = ctx.createLinearGradient(0, 0, w, 0);
    gradient3.addColorStop(0, "#F37037"); // orange
    gradient3.addColorStop(1, "#2C1561"); // dark blue

    const gradient4 = ctx.createLinearGradient(0, 0, w, 0);
    gradient4.addColorStop(0, "#4B1D70"); // dark purple
    gradient4.addColorStop(1, "#F37037"); // orange

    const gradients = [gradient1, gradient2, gradient3, gradient4];

    // Setup swing circle points
    for (let idx = 0; idx <= gradients.length - 1; idx++) {
      let swingpoints: Point[] = [];
      let radian = 0;

      for (let i = 0; i < points; i++) {
        radian = ((pi * 2) / points) * i;
        const ptX = center.x + radius * Math.cos(radian);
        const ptY = center.y + radius * Math.sin(radian);

        swingpoints.push({
          x: ptX,
          y: ptY,
          radian: radian,
          range: random(rangeMin, rangeMax),
          phase: 0
        });
      }

      circles.push(swingpoints);
    }

    function cycle(num1: number, num2: number): number {
      return ((num1 % num2) + num2) % num2;
    }

    function random(num1: number, num2: number): number {
      const max = Math.max(num1, num2);
      const min = Math.min(num1, num2);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    function drawCurve(pts: Point[], fillStyle: CanvasGradient) {
      ctx.fillStyle = fillStyle;
      ctx.beginPath();
      ctx.moveTo(
        (pts[cycle(-1, points)].x + pts[0].x) / 2,
        (pts[cycle(-1, points)].y + pts[0].y) / 2
      );
      for (let i = 0; i < pts.length; i++) {
        ctx.quadraticCurveTo(
          pts[i].x,
          pts[i].y,
          (pts[i].x + pts[cycle(i + 1, points)].x) / 2,
          (pts[i].y + pts[cycle(i + 1, points)].y) / 2
        );
      }

      ctx.closePath();
      ctx.fill();
    }

    function swingCircle() {
      ctx.clearRect(0, 0, w, h);

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "screen";

      for (let k = 0; k < circles.length; k++) {
        let swingpoints = circles[k];

        for (let i = 0; i < swingpoints.length; i++) {
          const multiplier = thinking || isInitializing ? 0.0125 : 0.005;
          swingpoints[i].phase += random(1, 5) * -multiplier;

          const phase = intensity / 100 + 0.5;

          const r =
            radius +
            swingpoints[i].range * phase * Math.sin(swingpoints[i].phase) -
            rangeMax;

          swingpoints[i].radian += pi / 700;

          const ptX = center.x + r * Math.cos(swingpoints[i].radian);
          const ptY = center.y + r * Math.sin(swingpoints[i].radian);

          swingpoints[i] = {
            x: ptX,
            y: ptY,
            radian: swingpoints[i].radian,
            range: swingpoints[i].range,
            phase: swingpoints[i].phase
          };
        }

        const fill = gradients[k];
        drawCurve(swingpoints, fill);
      }

      tickRef.current++;
      animationFrameRef.current = requestAnimationFrame(swingCircle);
    }

    animationFrameRef.current = requestAnimationFrame(swingCircle);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [intensity, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: size + "px",
        height: size + "px",
        margin: "0px",
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)"
      }}
    />
  );
};
